import * as Sentry from '@sentry/react'
import type { AxiosError, AxiosResponse } from 'axios'
import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { ObjectSchema } from 'yup'
import { Status } from '@/api/definition'

type ValidatorHook = {
  validate: <T>(
    schema: ObjectSchema<T>,
    response: AxiosResponse<unknown>,
  ) => Promise<T>
  showTransferError: (transfer: {
    status: number
    concerns?: Array<{ message: string }>
  }) => void
  showHttpError: (error: unknown) => void
}

const useResponseValidator = (): ValidatorHook => {
  const { t } = useTranslation('common')
  const { enqueueSnackbar } = useSnackbar()

  const showHttpError = useCallback(
    (error: unknown) => {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError
        if (axiosError.response) {
          const status = axiosError.response.status
          const responseData = axiosError.response.data

          let message = `Request failed with status code ${status}`
          if (typeof responseData === 'string') {
            message += `: ${responseData}`
          } else if (
            responseData &&
            typeof responseData === 'object' &&
            'message' in responseData
          ) {
            message += `: ${responseData.message}`
          }

          enqueueSnackbar(message, { variant: 'error' })
        }
      } else if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: 'error' })
      }

      if (error instanceof Error) {
        Sentry.captureException(error)
      }
    },
    [enqueueSnackbar],
  )

  const showTransferError = useCallback(
    (transfer: { status: number; concerns?: Array<{ message: string }> }) => {
      if (
        transfer.status === Status.FAILURE &&
        transfer.concerns?.[0]?.message
      ) {
        enqueueSnackbar(transfer.concerns[0].message, {
          variant: 'error',
        })
      }
    },
    [enqueueSnackbar],
  )

  const validate = useCallback(
    async <T>(
      schema: ObjectSchema<T>,
      response: AxiosResponse<unknown>,
    ): Promise<T> => {
      const { config, data } = response
      try {
        const validatedData = await schema.validate(data, {
          abortEarly: false,
          stripUnknown: true,
        })
        return validatedData
      } catch (error: unknown) {
        const { url } = config
        if (error instanceof Error) {
          enqueueSnackbar(t('request.invalidResponseData', { url }), {
            variant: 'error',
          })
          Sentry.captureException(error)
        } else {
          const fallbackError = new Error(
            `Unknown validation error for URL: ${url}`,
          )
          Sentry.captureException(fallbackError)
          enqueueSnackbar(t('request.unknownError', { url }), {
            variant: 'error',
          })
        }
        throw error
      }
    },
    [t, enqueueSnackbar],
  )

  return { validate, showTransferError, showHttpError }
}

export default useResponseValidator
